/**
 * Gestion de autenticacion Google OAuth PKCE en navegador (spec/05-security.md §4.2, ADR OQ-2).
 *
 * Principios no negociables:
 *  - Token de acceso custodiado unicamente en memoria de proceso (closure).
 *  - Sin token de refresco: el intercambio lo descarta activamente (rejectedRefreshToken).
 *  - state y codeVerifier efimeros en sessionStorage solo durante el redirect dance.
 *  - Cierre de sesion con purga total local (FR-STORE-8).
 */

import {
  type OAuthPkceConfig,
  generatePkcePair,
  generateState,
  buildAuthorizationUrl,
  validateCallback,
  exchangeCodeForToken,
} from "./oauth-pkce.js";
import { type TokenStore, createInMemoryTokenStore } from "./token-store.js";
import type { LocalStore } from "../storage/local-store.js";
import { FINVOZ_COLLECTIONS } from "../storage/indexeddb-factory.js";

export const GOOGLE_AUTH_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";
export const GOOGLE_TOKEN_ENDPOINT = "https://oauth2.googleapis.com/token";
export const GOOGLE_DRIVE_FILE_SCOPE = "https://www.googleapis.com/auth/drive.file";

export const DEFAULT_GOOGLE_SCOPES: readonly string[] = [
  GOOGLE_DRIVE_FILE_SCOPE,
];

export const GOOGLE_DEFAULT_REDIRECT_URI = "https://finvoz.pages.dev/";

/**
 * Client ID público de Google OAuth para FinVoz (cliente navegador sin backend, ADR OQ-2).
 * Prioridad de resolución:
 *  1. Inyección de build/entorno Vite (VITE_GOOGLE_CLIENT_ID en Cloudflare Pages / CI).
 *  2. Variable de entorno de proceso en entornos de test/Node.
 *  3. Client ID productivo por defecto registrado para https://finvoz.pages.dev/
 */
export const DEFAULT_GOOGLE_CLIENT_ID =
  ((typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID)) ||
  (typeof process !== "undefined" && process.env?.VITE_GOOGLE_CLIENT_ID) ||
  "219037328746-mot0rpjtik9ceuqant48dpnf0e2og0qp.apps.googleusercontent.com";

export function getGoogleOAuthRedirectUri(): string {
  if (typeof window === "undefined") return GOOGLE_DEFAULT_REDIRECT_URI;
  if (
    window.location.hostname === "finvoz.pages.dev" ||
    window.location.hostname.endsWith(".finvoz.pages.dev")
  ) {
    return GOOGLE_DEFAULT_REDIRECT_URI;
  }
  const pathname = window.location.pathname === "/index.html" ? "/" : window.location.pathname;
  const normalizedPath = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return `${window.location.origin}${normalizedPath}`;
}

const PKCE_STATE_KEY = "finvoz_pkce_state";
const PKCE_VERIFIER_KEY = "finvoz_pkce_verifier";

export function createGoogleOAuthConfig(
  clientId = DEFAULT_GOOGLE_CLIENT_ID,
  redirectUri = getGoogleOAuthRedirectUri(),
): OAuthPkceConfig {
  return {
    authorizationEndpoint: GOOGLE_AUTH_ENDPOINT,
    tokenEndpoint: GOOGLE_TOKEN_ENDPOINT,
    clientId,
    redirectUri,
    scopes: DEFAULT_GOOGLE_SCOPES,
  };
}

/**
 * Inicia el flujo OAuth PKCE redirigiendo el navegador a Google.
 */
export async function startGoogleOAuth(config: OAuthPkceConfig): Promise<void> {
  const state = generateState();
  const { codeVerifier, codeChallenge } = await generatePkcePair();

  // Guardar temporalmente en sessionStorage solo para correlacionar el retorno
  sessionStorage.setItem(PKCE_STATE_KEY, state);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

  const authUrl = buildAuthorizationUrl(config, state, codeChallenge);
  window.location.href = authUrl;
}

/**
 * Procesa el retorno del redirect OAuth si la URL contiene 'code' y 'state'.
 */
export async function handleGoogleOAuthCallback(
  config: OAuthPkceConfig,
  tokenStore: TokenStore,
  url = window.location.href,
): Promise<{ success: boolean; error?: string }> {
  const parsed = new URL(url);
  const code = parsed.searchParams.get("code");
  const receivedState = parsed.searchParams.get("state");
  const authError = parsed.searchParams.get("error");

  if (authError) {
    sessionStorage.removeItem(PKCE_STATE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    return { success: false, error: `Google OAuth denegado: ${authError}` };
  }

  if (!code || !receivedState) {
    return { success: false };
  }

  const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

  sessionStorage.removeItem(PKCE_STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  if (!expectedState || !codeVerifier) {
    return { success: false, error: "Estado PKCE no encontrado en sessionStorage" };
  }

  try {
    // Validar vinculacion de state y redirectUri real de retorno observado (spec/05-security.md §4.2)
    const cleanPath = parsed.pathname === "/index.html" ? "/" : parsed.pathname;
    const normalizedPath = cleanPath.endsWith("/") ? cleanPath : `${cleanPath}/`;
    const observedRedirectUri = `${parsed.origin}${normalizedPath}`;
    validateCallback(config, expectedState, receivedState, observedRedirectUri);

    const result = await exchangeCodeForToken(
      config,
      code,
      codeVerifier,
      async (u, init) => fetch(u, init),
    );

    tokenStore.set({
      accessToken: result.accessToken,
      expiresAt: result.expiresAt,
    });

    // Limpiar parametros OAuth de la URL sin recargar
    parsed.searchParams.delete("code");
    parsed.searchParams.delete("state");
    parsed.searchParams.delete("scope");
    parsed.searchParams.delete("prompt");
    window.history.replaceState({}, document.title, parsed.pathname + (parsed.search ? parsed.search : ""));

    return { success: true };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
}

/**
 * Cierre de sesion con borrado completo de datos locales (FR-STORE-8, spec/05-security.md §7).
 *  - El token de acceso en memoria se destruye.
 *  - Todas las colecciones del almacen local se purgan.
 *  - La hoja remota del usuario en Sheets NO se toca.
 */
export async function logoutAndPurgeLocalData(
  tokenStore: TokenStore,
  store: LocalStore,
): Promise<void> {
  tokenStore.clear();

  for (const collection of FINVOZ_COLLECTIONS) {
    const items = await store.list(collection);
    for (const item of items) {
      await store.delete(collection, item.id);
    }
  }

  sessionStorage.clear();
}
